import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Notify } from "@/components/ui/notify";
import { GlobalKPIStrip } from "@/features/dashboard/components/GlobalKPIStrip";
import { DashboardHead } from "@/features/dashboard/components/DashboardHead";
import { LineList } from "@/features/dashboard/components/LineList";
import { DashboardSectionBar } from "@/features/dashboard/components/DashboardSectionBar";
import { SelectionBar } from "@/features/dashboard/components/SelectionBar";
import { useDashboardViewState, useLinesOeeMap } from "@/features/dashboard/hooks";
import { filterLines, sortLines } from "@/features/dashboard/lib/dashboard-filters";
import { buildLinesCsv, downloadCsv } from "@/features/dashboard/lib/dashboard-export";
import { useLines, type MachineDetail } from "@/features/lines/hooks";
import { useAuthStore } from "@/stores/auth.store";
import { useRealtimeRooms } from "@/lib/use-realtime-rooms";
import { MachineSidebar } from "@/features/machine-sidebar/MachineSidebar";
import type { DashboardLineView } from "@/types/dashboard.types";

export function Dashboard(): React.ReactElement {
  const me = useAuthStore((s) => s.user)!;
  const qc = useQueryClient();
  const linesQuery = useLines();
  const { data: lines, isLoading } = linesQuery;
  const view = useDashboardViewState();
  const [selected, setSelected] = React.useState<{ machine: MachineDetail; lineId: string } | null>(
    null
  );

  const baseLines = React.useMemo(() => (lines ?? []) as DashboardLineView[], [lines]);
  const lineIds = React.useMemo(() => baseLines.map((l) => l.id), [baseLines]);
  // Entra nos rooms de todas as linhas carregadas (room-per-line) p/ eventos ao vivo.
  useRealtimeRooms(lineIds);
  const oeeMap = useLinesOeeMap(lineIds);

  const visible = React.useMemo(() => {
    const enriched = baseLines.map((l) => ({
      ...l,
      oee: oeeMap[l.id]?.oee ?? null,
      availability: oeeMap[l.id]?.availability ?? null,
      performance: oeeMap[l.id]?.performance ?? null,
      quality: oeeMap[l.id]?.quality ?? null,
      planAdherence: oeeMap[l.id]?.planAdherence ?? null,
    }));
    return sortLines(
      filterLines(enriched, {
        statusFilter: view.statusFilter,
        query: view.query,
        responsibleFilter: view.responsibleFilter,
      }),
      view.sort
    );
  }, [baseLines, oeeMap, view.statusFilter, view.query, view.responsibleFilter, view.sort]);
  const visibleIds = React.useMemo(() => visible.map((l) => l.id), [visible]);

  const handleExport = () => {
    const sel = visible.filter((l) => view.selectedIds.has(l.id));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`linhas-${stamp}.csv`, buildLinesCsv(sel));
  };

  const handleRefresh = () => {
    for (const key of [["lines"], ["oee"], ["production"], ["dashboard"]]) {
      void qc.invalidateQueries({ queryKey: key });
    }
  };

  const welcomeName = me.name?.trim() ?? "";
  const timestamp = React.useMemo(
    () =>
      new Date().toLocaleString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
      }),
    []
  );

  return (
    <section className="space-y-6">
      <DashboardHead
        userName={welcomeName}
        role={me.role}
        timestamp={timestamp}
        onRefresh={handleRefresh}
      />

      <GlobalKPIStrip
        lines={lines}
        loading={isLoading}
        linesIsError={linesQuery.isError}
        linesError={linesQuery.error}
        onRetryLines={() => void linesQuery.refetch()}
      />

      <DashboardSectionBar
        lines={baseLines}
        visibleIds={visibleIds}
        visibleCount={visible.length}
        view={view}
      />

      {view.selectedIds.size > 0 ? (
        <SelectionBar
          count={view.selectedIds.size}
          onExport={handleExport}
          onClear={view.clearSelection}
        />
      ) : null}

      {linesQuery.isError ? (
        <Notify.QueryError error={linesQuery.error} onRetry={() => void linesQuery.refetch()} />
      ) : isLoading ? (
        <p className="text-sm text-text-secondary">Carregando…</p>
      ) : (
        <LineList
          lines={visible}
          expandedIds={view.expandedIds}
          onToggleExpand={view.toggleExpand}
          onMachineClick={(m, lineId) => setSelected({ machine: m, lineId })}
          selectedIds={view.selectedIds}
          onToggleSelect={view.toggleSelect}
          emptyFiltered={(lines?.length ?? 0) > 0}
        />
      )}

      {selected ? (
        <MachineSidebar
          machineId={selected.machine.id}
          lineId={selected.lineId}
          mode="view"
          context="dashboard"
          onClose={() => setSelected(null)}
        />
      ) : null}
    </section>
  );
}
